import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ProfileModal from "../../components/ProfileModal";
import ReportModal from "../../components/ReportModal";
import AppointmentDetailsModal from "../../components/AppointmentDetailsModal";
import AdminCreateAppointmentModal from "../../components/AdminCreateAppointmentModal";
import UserInfoModal from "../../components/UserInfoModal";
import DashboardSidebar from "../../components/DashboardSidebar";
import { generateFakeAppointments, generateFakeServiceWorkers, generateFakeUsers } from "./fakeDataGenerator";
import "./Dashboard.css";
import { BACKEND_URL } from "../../utils/config.js";
import { checkIsLoggedIn, handleFetchResponse } from "./helpers.js";

const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || "/backend";

// Helper function to get formatted service names from appointment
const getServiceNames = (appointment) => {
  // Handle new format: service_names array
  if (appointment.service_names && Array.isArray(appointment.service_names)) {
    return appointment.service_names.join(', ');
  } else if (appointment.services && Array.isArray(appointment.services)) {
    return appointment.services.join(', ');
    }

  // Handle old format: single service_name string
  if (appointment.service_name) {
    return appointment.service_name;
  }
  // Fallback to service field
  if (appointment.service) {
    return appointment.service;
  }
  return 'N/A';
};

export default function AdminDashboard() {
    const [allAppointments, setAllAppointments] = useState([]);
    const [upcomingAppointments, setUpcomingAppointments] = useState([]);
    const [pastAppointments, setPastAppointments] = useState([]);
    const [filteredUpcomingAppointments, setFilteredUpcomingAppointments] = useState([]);
    const [filteredPastAppointments, setFilteredPastAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [showDetailsMenu, setShowDetailsMenu] = useState(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
    const menuHideTimeoutRef = useRef(null);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [showAppointmentDetails, setShowAppointmentDetails] = useState(false);
    const [showUserInfo, setShowUserInfo] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    
    // Filter states
    const [filterType, setFilterType] = useState('all'); // 'all', 'service_worker', 'patient'
    const [filterValue, setFilterValue] = useState('');
    const [availableServiceWorkers, setAvailableServiceWorkers] = useState([]);
    const [availableUsers, setAvailableUsers] = useState([]);
    
    // Upcoming appointments filter states
    const [upcomingStatusFilter, setUpcomingStatusFilter] = useState('all');
    const [upcomingServiceFilter, setUpcomingServiceFilter] = useState('all');
    const [availableServices, setAvailableServices] = useState([]);
    
    // User information states
    const [serviceWorkers, setServiceWorkers] = useState([]);
    const [users, setUsers] = useState([]);
    const [statuses, setStatuses] = useState([]);
    
    // Tooltip state
    const [hoveredBar, setHoveredBar] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    
    // Memoized activity data to prevent regeneration on every render
    const [activityData, setActivityData] = useState([]);
    
    // Activity section filter states
    const [activityStatusFilter, setActivityStatusFilter] = useState('all'); // 'all', 'pending', 'assigned', 'accepted', 'rejected', 'serviced', 'reviewed', 'completed', 'cancelled'
    const [activityPswFilter, setActivityPswFilter] = useState('all'); // 'all' or specific psw_id
    const [activityUserFilter, setActivityUserFilter] = useState('all'); // 'all' or specific user_id
    const [activityLoading, setActivityLoading] = useState(false);
    
    // Assign service worker modal state
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [appointmentToAssign, setAppointmentToAssign] = useState(null);
    const [selectedServiceWorker, setSelectedServiceWorker] = useState('');
    const [assignmentLoading, setAssignmentLoading] = useState(false);
    const [assignmentError, setAssignmentError] = useState(null);
    
    // Report modal state
    const [showReportModal, setShowReportModal] = useState(false);
    const [selectedReportId, setSelectedReportId] = useState(null);
    
    const [error, setError] = useState(null);
    
    // Profile modal state
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [profileData, setProfileData] = useState({
        email: '',
        username: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        date_of_birth: '',
        address: ''
    });
    const [profileFormData, setProfileFormData] = useState({
        email: '',
        username: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        date_of_birth: '',
        address: ''
    });
    const [profileLoading, setProfileLoading] = useState(false);
    const [profileError, setProfileError] = useState(null);
    const [profileSuccess, setProfileSuccess] = useState(null);
    
    // Invite modal state
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteType, setInviteType] = useState('psw'); // 'user' or 'psw'
    const [inviteLoading, setInviteLoading] = useState(false);
    const [inviteError, setInviteError] = useState(null);
    const [inviteSuccess, setInviteSuccess] = useState(null);
    
    // Create appointment modal state
    const [showCreateAppointmentModal, setShowCreateAppointmentModal] = useState(false);
    const [createAppointmentFormData, setCreateAppointmentFormData] = useState({
        date: '',
        start_time: '',
        end_time: '',
        services: [],
        description: '',
        location: '',
        user_id: '',
        psw_id: '',
        psw_name: ''
    });
    const [createAppointmentLoading, setCreateAppointmentLoading] = useState(false);
    const [createAppointmentError, setCreateAppointmentError] = useState(null);
    const [isEditingAppointment, setIsEditingAppointment] = useState(false);
    const [appointmentBeingEdited, setAppointmentBeingEdited] = useState(null);
    
    const navigate = useNavigate();

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                let isLoggedin = await checkIsLoggedIn();
                if (!isLoggedin || localStorage.length === 0) {
                    navigate(`${FRONTEND_URL}/login`);
                    localStorage.clear();
                }

                // Read USE_FAKE_DATA from environment, default to true if missing
                const USE_FAKE_DATA = process.env.REACT_APP_USE_FAKE_DATA !== 'false';
                
                let processedAppointments = [];
                let fetchedServiceWorkers = [];
                let fetchedUsers = [];
                let fetchedStatuses = ['Pending', 'Assigned', 'Accepted', 'Rejected', 'Serviced', 'Reviewed', 'Completed', 'Cancelled'];
                
                if (USE_FAKE_DATA) {
                    // Use fake data for testing
                    processedAppointments = generateFakeAppointments(fetchedStatuses);
                    fetchedServiceWorkers = generateFakeServiceWorkers();
                    fetchedUsers = generateFakeUsers();
                } else {
                    // Fetch all appointments
                    const appointmentsRes = await fetch(`${BACKEND_URL}/appointments/all`, {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        credentials: "include",
                    });

                    const checkedAppointmentsRes = await handleFetchResponse(appointmentsRes, navigate);
                    if (!checkedAppointmentsRes) return;

                    if (!checkedAppointmentsRes.ok) {
                        throw new Error(`Failed to fetch appointments: ${checkedAppointmentsRes.status}`);
                    }

                    const appointmentsData = await checkedAppointmentsRes.json();
                    processedAppointments = appointmentsData.appointments.map(a => ({
                        ...a,
                        id: a.appointment_id 
                    }));

                    // Fetch service workers
                    try {
                        const swRes = await fetch(`${BACKEND_URL}/admin/psws`, {
                            method: "GET",
                            headers: {
                                "Content-Type": "application/json",
                            },
                            credentials: "include",
                        });
                        const checkedSwRes = await handleFetchResponse(swRes, navigate);
                        if (checkedSwRes && checkedSwRes.ok) {
                            const swData = await checkedSwRes.json();
                            // Handle the response format with users array
                            const swArray = Array.isArray(swData) ? swData : (Array.isArray(swData.users) ? swData.users : (Array.isArray(swData.service_workers) ? swData.service_workers : []));
                            // Map the fields to match the expected format
                            fetchedServiceWorkers = swArray.map(sw => ({
                                psw_id: sw.user_id || sw.psw_id || sw.id,
                                id: sw.user_id || sw.psw_id || sw.id,
                                user_id: sw.user_id,
                                first_name: sw.first_name,
                                last_name: sw.last_name,
                                name: `${sw.first_name || ''} ${sw.last_name || ''}`.trim(),
                                email: sw.email,
                                phone_number: sw.phone_number
                            }));
                        }
                    } catch (err) {
                        console.error('Error fetching service workers:', err);
                    }

                    // Fetch users
                    try {
                        const usersRes = await fetch(`${BACKEND_URL}/admin/users`, {
                            method: "GET",
                            headers: {
                                "Content-Type": "application/json",
                            },
                            credentials: "include",
                        });
                        const checkedUsersRes = await handleFetchResponse(usersRes, navigate);
                        if (checkedUsersRes && checkedUsersRes.ok) {
                            const usersData = await checkedUsersRes.json();
                            fetchedUsers = Array.isArray(usersData) ? usersData : (Array.isArray(usersData.users) ? usersData.users : []);
                        }
                    } catch (err) {
                        console.error('Error fetching users:', err);
                    }

                    // Fetch statuses
                    try {
                        const statusesRes = await fetch(`${BACKEND_URL}/appointments/statuses`, {
                            method: "GET",
                            headers: {
                                "Content-Type": "application/json",
                            },
                            credentials: "include",
                        });
                        const checkedStatusesRes = await handleFetchResponse(statusesRes, navigate);
                        if (checkedStatusesRes && checkedStatusesRes.ok) {
                            const statusesData = await checkedStatusesRes.json();
                            fetchedStatuses = Array.isArray(statusesData) ? statusesData : (Array.isArray(statusesData.statuses) ? statusesData.statuses : []);
                        }
                    } catch (err) {
                        console.error('Error fetching statuses:', err);
                    }
                }

                setStatuses(fetchedStatuses);

                setAllAppointments(processedAppointments);
                setServiceWorkers(fetchedServiceWorkers);
                setUsers(fetchedUsers);

                // Filter appointments into upcoming and past
                const now = new Date();

                const upcomingApts = processedAppointments.filter(apt => 
                    new Date(apt.start_date) >= now
                ).sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
                const pastApts = processedAppointments.filter(apt => 
                    new Date(apt.start_date) < now
                ).sort((a, b) => new Date(b.start_date) - new Date(a.start_date));

                setUpcomingAppointments(upcomingApts);
                setPastAppointments(pastApts);
                setFilteredUpcomingAppointments(upcomingApts);
                setFilteredPastAppointments(pastApts);
                
                // Fetch available services from backend
                try {
                    const servicesRes = await fetch(`${BACKEND_URL}/others/services`, {
                        credentials: 'include'
                    });
                    const checkedServicesRes = await handleFetchResponse(servicesRes, navigate);
                    if (checkedServicesRes && checkedServicesRes.ok) {
                        const servicesData = await checkedServicesRes.json();
                        setAvailableServices(Array.isArray(servicesData) ? servicesData : []);
                    } else if (checkedServicesRes !== null) {
                        // Fallback to extracting services from appointments
                        const uniqueServices = new Set();
                        upcomingApts.forEach(apt => {
                            if (apt.service_names && Array.isArray(apt.service_names)) {
                                apt.service_names.forEach(service => uniqueServices.add(service));
                            } else if (apt.services && Array.isArray(apt.services)) {
                                apt.services.forEach(service => uniqueServices.add(service));
                            };
                            if (apt.service_name) uniqueServices.add(apt.service_name);
                            if (apt.service) uniqueServices.add(apt.service);
                        });
                        setAvailableServices(Array.from(uniqueServices).sort());
                    }
                } catch (err) {
                    console.error('Error fetching services:', err);
                    // Fallback to extracting services from appointments
                    const uniqueServices = new Set();
                    upcomingApts.forEach(apt => {
                        if (apt.service_names && Array.isArray(apt.service_names)) {
                            apt.service_names.forEach(service => uniqueServices.add(service));
                        }
                        if (apt.service_name) uniqueServices.add(apt.service_name);
                        if (apt.service) uniqueServices.add(apt.service);
                    });
                    setAvailableServices(Array.from(uniqueServices).sort());
                }
                
                // Extract unique service workers and users from appointments
                const uniqueSWs = new Map();
                const uniqueUsers = new Map();
                
                processedAppointments.forEach(apt => {
                    if (apt.psw_id) uniqueSWs.set(apt.psw_id, null);
                    if (apt.user_id) uniqueUsers.set(apt.user_id, null);
                });
                
                setAvailableServiceWorkers(Array.from(uniqueSWs.keys()).map(id => ({
                    psw_id: id,
                    id: id,
                    user_id: id,
                    name: fetchedServiceWorkers.find(sw => sw.user_id === id || sw.psw_id === id || sw.id === id)?.name || id
                })));
                
                setAvailableUsers(Array.from(uniqueUsers.keys()).map(id => ({
                    user_id: id,
                    id: id,
                    name: fetchedUsers.find(p => p.user_id === id || p.id === id)?.name || 
                          `${fetchedUsers.find(p => p.user_id === id || p.id === id)?.first_name || ''} ${fetchedUsers.find(p => p.user_id === id || p.id === id)?.last_name || ''}`.trim() || id
                })));

            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
    }, [BACKEND_URL]);

    useEffect(() => {
        const filterAppointments = (appointments) => {
            if (filterType === 'all') {
                return appointments;
            }

            if (!filterValue) {
                return appointments;
            }

            let filtered = [];
            if (filterType === 'service_worker') {
                filtered = appointments.filter(apt => 
                    apt.psw_id === filterValue || 
                    apt.psw_id?.toLowerCase().includes(filterValue.toLowerCase())
                );
            } else if (filterType === 'patient') {
                filtered = appointments.filter(apt => {
                    const userOrPatientId = apt.user_id;
                    return userOrPatientId === filterValue ||
                           userOrPatientId?.toLowerCase().includes(filterValue.toLowerCase());
                });
            }

            return filtered;
        };

        setFilteredUpcomingAppointments(filterAppointments(upcomingAppointments));
        setFilteredPastAppointments(filterAppointments(pastAppointments));
    }, [filterType, filterValue, upcomingAppointments, pastAppointments]);

    const formatDateTime = (isoString) => {
        if (!isoString) return { date: 'N/A', time: 'N/A' };
        try {
            const dateObj = new Date(isoString);
            if (isNaN(dateObj.getTime())) {
                return { date: 'Invalid Date', time: 'Invalid Time' };
            }
            const date = dateObj.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
            const time = dateObj.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true });
            return { date, time };
        } catch (error) {
            console.error('Error formatting date:', error, isoString);
            return { date: 'N/A', time: 'N/A' };
        }
    };

    const handleLogout = () => {
        // Clear user data from localStorage
        localStorage.removeItem('username');
        localStorage.removeItem('user_id');
        localStorage.removeItem('role');
        
        // Redirect to login page
        navigate(`${FRONTEND_URL}/login`);
    };

    const getServiceWorkerName = (pswId) => {
        const sw = serviceWorkers.find(s => s.user_id === pswId || s.psw_id === pswId || s.id === pswId);
        return sw ? (sw.name || `${sw.first_name || ''} ${sw.last_name || ''}`.trim() || pswId) : pswId || 'N/A';
    };

    const getPatientName = (userId) => {
        const patient = users.find(p => p.user_id === userId || p.id === userId);
        return patient ? (patient.name || `${patient.first_name || ''} ${patient.last_name || ''}`.trim() || userId) : userId || 'N/A';
    };

    const handleViewReports = () => {
        navigate(`${FRONTEND_URL}/admin-reports`);
    };

    const handleOpenProfileModal = async () => {
        try {
            // Load current user profile data from localStorage or fetch from backend
            const currentUsername = localStorage.getItem('username') || '';
            const currentUserId = localStorage.getItem('user_id') || '';
            
            // Try to fetch from backend if available
            let userData = {
                email: localStorage.getItem('email') || '',
                username: currentUsername,
                first_name: localStorage.getItem('first_name') || '',
                last_name: localStorage.getItem('last_name') || '',
                phone_number: localStorage.getItem('phone_number') || '',
                date_of_birth: localStorage.getItem('date_of_birth') || '',
                address: localStorage.getItem('address') || ''
            };

            // First, fetch user details from /admin/user/{user_id}
            if (currentUserId) {
                try {
                    const userRes = await fetch(`${BACKEND_URL}/admin/user/${currentUserId}`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        credentials: 'include',
                    });

                    const checkedUserRes = await handleFetchResponse(userRes, navigate);
                    if (checkedUserRes && checkedUserRes.ok) {
                        const userDetailsData = await checkedUserRes.json();
                        // Merge user details from /admin/user endpoint
                        userData = {
                            ...userData,
                            ...(userDetailsData.user || userDetailsData),
                        };
                    }
                } catch (err) {
                    console.log('Could not fetch user details from /admin/user endpoint');
                }
            }

            // Then, attempt to fetch fresh data from /admin/profile
            try {
                const profileRes = await fetch(`${BACKEND_URL}/admin/profile`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                });

                const checkedProfileRes = await handleFetchResponse(profileRes, navigate);
                if (checkedProfileRes && checkedProfileRes.ok) {
                    const profileData = await checkedProfileRes.json();
                    // Profile data takes precedence over user data for overlapping fields
                    userData = {
                        ...userData,
                        ...(profileData.user || profileData),
                    };
                }
            } catch (err) {
                console.log('Using previously fetched data for profile');
            }

            setProfileData(userData);
            setProfileFormData(userData);
            setShowProfileModal(true);
            setProfileError(null);
            setProfileSuccess(null);
        } catch (err) {
            setProfileError('Failed to load profile');
            console.error('Error loading profile:', err);
        }
    };

    const handleCloseProfileModal = () => {
        setShowProfileModal(false);
        setProfileFormData(profileData);
        setProfileError(null);
        setProfileSuccess(null);
    };

    const handleProfileInputChange = (e) => {
        const { name, value } = e.target;
        setProfileFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmitProfile = async () => {
        setProfileLoading(true);
        setProfileError(null);
        setProfileSuccess(null);

        try {
            const response = await fetch(`${BACKEND_URL}/users/update`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(profileFormData)
            });

            const checkedResponse = await handleFetchResponse(response, navigate);
            if (!checkedResponse) return;

            if (!checkedResponse.ok) {
                const errorData = await checkedResponse.json();
                throw new Error(errorData.message || `Failed to update profile: ${checkedResponse.status}`);
            }

            const result = await response.json();
            
            // Update localStorage with new values
            Object.keys(profileFormData).forEach(key => {
                localStorage.setItem(key, profileFormData[key]);
            });

            setProfileData(profileFormData);
            setProfileSuccess('Profile updated successfully!');
            
            // Close modal after success
            setTimeout(() => {
                handleCloseProfileModal();
            }, 1500);
        } catch (err) {
            setProfileError(err.message);
            console.error('Error updating profile:', err);
        } finally {
            setProfileLoading(false);
        }
    };

    const handleViewUserInfo = async (userId, userType) => {
        try {
            let userData = { type: userType };
            
            // First, fetch user details from /admin/user/{userId}
            if (userId) {
                try {
                    const userRes = await fetch(`${BACKEND_URL}/admin/user/${userId}`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        credentials: 'include',
                    });

                    const checkedUserRes = await handleFetchResponse(userRes, navigate);
                    if (checkedUserRes && checkedUserRes.ok) {
                        const userDetailsData = await checkedUserRes.json();
                        userData = { 
                            ...userData, 
                            ...(userDetailsData.user || userDetailsData) 
                        };
                    }
                } catch (err) {
                    console.log('Could not fetch user details from /admin/user endpoint');
                }
            }
            
            // Then, fetch additional info from /admin/profile
            try {
                const profileRes = await fetch(`${BACKEND_URL}/admin/profile`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify({
                        id: userId,
                        user_type: userType === 'service_worker' ? 'psw' : 'patient'
                    })
                });
                
                const checkedProfileRes = await handleFetchResponse(profileRes, navigate);
                if (checkedProfileRes && checkedProfileRes.ok) {
                    const profileData = await checkedProfileRes.json();
                    userData = { 
                        ...userData, 
                        ...(profileData.user || profileData)
                    };
                }
            } catch (err) {
                console.log('Could not fetch user profile info');
            }
            
            setSelectedUser(userData);
            setShowUserInfo(true);
        } catch (err) {
            console.error('Error fetching user info:', err);
        }
    };

    const handleViewDetails = (appointmentId, e) => {
        if (!appointmentId) {
            console.error('Appointment ID is missing');
            return;
        }
        
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        
        const isOpen = showDetailsMenu === appointmentId;
        setShowDetailsMenu(isOpen ? null : appointmentId);
        
        if (!isOpen && e && e.currentTarget) {
            updateMenuPosition(appointmentId, e.currentTarget.closest('.details-menu-container'));
        }
    };

    const updateMenuPosition = (appointmentId, containerElement) => {
        if (!containerElement) return;
        
        setTimeout(() => {
            try {
                // Find the button element within the container
                const buttonElement = containerElement.querySelector('.details-button');
                if (!buttonElement) return;
                
                const buttonRect = buttonElement.getBoundingClientRect();
                const menuHeight = 150; // Approximate menu height
                const viewportHeight = window.innerHeight;
                const spaceBelow = viewportHeight - buttonRect.bottom;
                const spaceAbove = buttonRect.top;
                
                let top = buttonRect.bottom + 8;
                const right = window.innerWidth - buttonRect.right;
                
                // If not enough space below but enough space above, open upward
                if (spaceBelow < menuHeight + 20 && spaceAbove > menuHeight + 20) {
                    top = buttonRect.top - menuHeight - 8;
                }
                
                setMenuPosition({ top, right });
            } catch (error) {
                console.error('Error positioning menu:', error);
            }
        }, 10);
    };

    const handleMouseEnter = (appointmentId, e) => {
        if (!appointmentId) {
            console.error('Appointment ID is missing');
            return;
        }
        
        // Clear any pending hide timeout
        if (menuHideTimeoutRef.current) {
            clearTimeout(menuHideTimeoutRef.current);
            menuHideTimeoutRef.current = null;
        }
        
        if (e) {
            e.stopPropagation();
        }
        
        setShowDetailsMenu(appointmentId);
        updateMenuPosition(appointmentId, e?.currentTarget);
    };

    const handleMouseLeave = () => {
        // Add a delay before hiding the menu (300ms) to give user time to move to menu
        menuHideTimeoutRef.current = setTimeout(() => {
            setShowDetailsMenu(null);
            menuHideTimeoutRef.current = null;
        }, 300);
    };

    const handleCloseDetailsMenu = () => {
        setShowDetailsMenu(null);
    };

    const handleCloseAppointmentDetails = () => {
        setShowAppointmentDetails(false);
        setSelectedAppointment(null);
    };

    const isAppointmentUpcoming = (appointment) => {
        if (!appointment || !appointment.start_date) return false;
        const appointmentDate = new Date(appointment.start_date);
        const now = new Date();
        return appointmentDate > now;
    };

    const resetAppointmentForm = () => {
        setCreateAppointmentFormData({
            date: '',
            start_time: '',
            end_time: '',
            services: [],
            description: '',
            location: '',
            user_id: '',
            psw_id: '',
            psw_name: ''
        });
        setIsEditingAppointment(false);
        setAppointmentBeingEdited(null);
        setCreateAppointmentError(null);
    };

    const handleEditAppointment = (appointment) => {
        if (!appointment) return;

        // Parse the start and end dates to extract date and time
        const startDate = new Date(appointment.start_date);
        const endDate = new Date(appointment.end_date);
        
        // Format date as YYYY-MM-DD
        const dateStr = startDate.toISOString().split('T')[0];
        
        // Format time as HH:MM
        const startTimeStr = String(startDate.getHours()).padStart(2, '0') + ':' + String(startDate.getMinutes()).padStart(2, '0');
        const endTimeStr = String(endDate.getHours()).padStart(2, '0') + ':' + String(endDate.getMinutes()).padStart(2, '0');
        
        // Get services array
        const services = appointment.service_names || appointment.services || [];
        
        // Populate form with appointment data
        setCreateAppointmentFormData({
            date: dateStr,
            start_time: startTimeStr,
            end_time: endTimeStr,
            services: Array.isArray(services) ? services : [services].filter(s => s),
            description: appointment.description || '',
            location: appointment.location || '',
            user_id: appointment.user_id || '',
            psw_id: appointment.psw_id || '',
            psw_name: appointment.psw_name || ''
        });
        
        setAppointmentBeingEdited(appointment);
        setIsEditingAppointment(true);
        setCreateAppointmentError(null);
        setShowCreateAppointmentModal(true);
    };

    const handleDeleteAppointment = async (appointment) => {
        if (!appointment || !appointment.id) {
            console.error('No appointment selected');
            return;
        }

        const confirmDelete = window.confirm(
            `Are you sure you want to delete this appointment? This action cannot be undone.`
        );

        if (!confirmDelete) {
            return;
        }

        try {
            const res = await fetch(`${BACKEND_URL}/appointments/${appointment.id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const checkedRes = await handleFetchResponse(res, navigate);
            if (!checkedRes) return;

            if (!checkedRes.ok) {
                throw new Error(`Failed to delete appointment: ${checkedRes.status}`);
            }

            // Remove from all appointments
            setAllAppointments(prevAllAppointments =>
                prevAllAppointments.filter(apt => apt.id !== appointment.id)
            );

            // Remove from upcoming appointments
            setUpcomingAppointments(prevUpcomingAppointments =>
                prevUpcomingAppointments.filter(apt => apt.id !== appointment.id)
            );

            // Remove from past appointments
            setPastAppointments(prevPastAppointments =>
                prevPastAppointments.filter(apt => apt.id !== appointment.id)
            );

            // Close the details modal
            handleCloseAppointmentDetails();

            alert('Appointment deleted successfully!');
        } catch (err) {
            console.error('Error deleting appointment:', err);
            alert(`Failed to delete appointment: ${err.message}`);
        }
    };

    const handleOpenAssignModal = (appointment, e) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        setAppointmentToAssign(appointment);
        setSelectedServiceWorker(appointment.psw_id || '');
        setAssignmentError(null);
        setShowAssignModal(true);
        setShowDetailsMenu(null);
    };

    const handleCloseAssignModal = () => {
        setShowAssignModal(false);
        setAppointmentToAssign(null);
        setSelectedServiceWorker('');
        setAssignmentError(null);
    };

    const handleSubmitAssignment = async () => {
        if (!selectedServiceWorker) {
            setAssignmentError('Please select a service worker');
            return;
        }

        setAssignmentLoading(true);
        setAssignmentError(null);

        try {
            const response = await fetch(`${BACKEND_URL}/appointments/assign`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    appointment_id: appointmentToAssign.id,
                    psw_id: selectedServiceWorker
                })
            });

            const checkedResponse = await handleFetchResponse(response, navigate);
            if (!checkedResponse) return;

            if (!checkedResponse.ok) {
                const errorData = await checkedResponse.json();
                throw new Error(errorData.message || `Failed to assign service worker: ${checkedResponse.status}`);
            }

            // Update the appointment in the state
            setAllAppointments(prevAppointments =>
                prevAppointments.map(apt =>
                    apt.id === appointmentToAssign.id
                        ? { ...apt, psw_id: selectedServiceWorker }
                        : apt
                )
            );

            handleCloseAssignModal();
        } catch (err) {
            setAssignmentError(err.message);
            console.error('Error assigning service worker:', err);
        } finally {
            setAssignmentLoading(false);
        }
    };

    const handleCloseUserInfo = () => {
        setShowUserInfo(false);
        setSelectedUser(null);
    };

    const handleInvite = async () => {
        if (!inviteEmail.trim()) {
            setInviteError('Email is required');
            return;
        }

        setInviteLoading(true);
        setInviteError(null);
        setInviteSuccess(null);

        try {
            const res = await fetch(`${BACKEND_URL}/admin/invite`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ 
                    email: inviteEmail.trim(), 
                    is_psw: inviteType === 'psw',
                    return_url: `${window.location.origin}${FRONTEND_URL}`,
                    timestamp: new Date().toISOString() 
                })
            });

            const checkedRes = await handleFetchResponse(res, navigate);
            if (!checkedRes) return;

            if (!checkedRes.ok) {
                throw new Error(`Failed to send invite: ${checkedRes.status}`);
            }

            setInviteSuccess(`Invite sent to ${inviteEmail}`);
            setInviteEmail('');
            setTimeout(() => {
                setShowInviteModal(false);
                setInviteSuccess(null);
            }, 2000);
        } catch (err) {
            setInviteError(err.message || 'Failed to send invite');
        } finally {
            setInviteLoading(false);
        }
    };

    const handleCreateAppointment = async (e) => {
        e.preventDefault();
        
        // Validate form
        if (
            !createAppointmentFormData.date ||
            !createAppointmentFormData.start_time ||
            !createAppointmentFormData.end_time ||
            !createAppointmentFormData.user_id ||
            !createAppointmentFormData.location
        ) {
            setCreateAppointmentError('Please fill in all required fields');
            return;
        }

        if (!createAppointmentFormData.location.trim()) {
            setCreateAppointmentError('Location is required');
            return;
        }

        if (createAppointmentFormData.services.length === 0) {
            setCreateAppointmentError('Please select at least one service');
            return;
        }

        const startDateTime = new Date(`${createAppointmentFormData.date}T${createAppointmentFormData.start_time}`);
        const endDateTime = new Date(`${createAppointmentFormData.date}T${createAppointmentFormData.end_time}`);
        const now = new Date();

        if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
            setCreateAppointmentError('Please provide valid start and end times');
            return;
        }

        if (endDateTime <= startDateTime) {
            setCreateAppointmentError('End time must be after start time');
            return;
        }

        setCreateAppointmentLoading(true);
        setCreateAppointmentError(null);

        try {
            const appointmentData = {
                user_id: createAppointmentFormData.user_id,
                psw_id: createAppointmentFormData.psw_id || null,
                psw_name: createAppointmentFormData.psw_name || null,
                start_date: startDateTime.toISOString(),
                end_date: endDateTime.toISOString(),
                status: isEditingAppointment ? appointmentBeingEdited.status : 'Pending',
                services: createAppointmentFormData.services,
                description: createAppointmentFormData.description,
                location: createAppointmentFormData.location
            };

            // Determine endpoint and method based on whether we're editing or creating
            const endpoint = isEditingAppointment 
                ? `${BACKEND_URL}/appointments/${appointmentBeingEdited.id}`
                : `${BACKEND_URL}/appointments/create`;
            const method = isEditingAppointment ? 'POST' : 'PUT';

            // Wrap the data in the Update enum format for edit requests
            const requestBody = isEditingAppointment 
                ? { All: appointmentData }
                : appointmentData;

            const res = await fetch(endpoint, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(requestBody)
            });

            const checkedRes = await handleFetchResponse(res, navigate);
            if (!checkedRes) return;

            if (!checkedRes.ok) {
                throw new Error(`Failed to ${isEditingAppointment ? 'update' : 'create'} appointment: ${checkedRes.status}`);
            }

            if (isEditingAppointment) {
                // Update the appointment in the list
                const updatedAppointment = {
                    ...appointmentBeingEdited,
                    ...appointmentData
                };

                setAllAppointments(prevAllAppointments => 
                    prevAllAppointments.map(apt => apt.id === appointmentBeingEdited.id ? updatedAppointment : apt)
                );

                setUpcomingAppointments(prevUpcomingAppointments => 
                    prevUpcomingAppointments.map(apt => apt.id === appointmentBeingEdited.id ? updatedAppointment : apt)
                        .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
                );

                setPastAppointments(prevPastAppointments => 
                    prevPastAppointments.map(apt => apt.id === appointmentBeingEdited.id ? updatedAppointment : apt)
                        .sort((a, b) => new Date(b.start_date) - new Date(a.start_date))
                );

                alert('Appointment updated successfully!');
            } else {
                // Add to appointments list
                const appointmentWithId = {
                    ...appointmentData,
                    id: Date.now().toString()
                };

                setAllAppointments(prevAllAppointments => [...prevAllAppointments, appointmentWithId]);
                
                // Update upcoming appointments list
                if (startDateTime >= new Date()) {
                    setUpcomingAppointments(prevUpcomingAppointments => [...prevUpcomingAppointments, appointmentWithId].sort((a, b) => 
                        new Date(a.start_date) - new Date(b.start_date)
                    ));
                }

                alert('Appointment created successfully!');
            }

            // Close modal and reset form
            setShowCreateAppointmentModal(false);
            resetAppointmentForm();
        } catch (err) {
            console.error(`Error ${isEditingAppointment ? 'updating' : 'creating'} appointment:`, err);
            setCreateAppointmentError(err.message || `Failed to ${isEditingAppointment ? 'update' : 'create'} appointment`);
        } finally {
            setCreateAppointmentLoading(false);
        }
    };

    const handleMarkAsComplete = async (appointment) => {
        if (!appointment || !appointment.id) {
            console.error('No appointment selected');
            return;
        }

        try {
            const response = await fetch(`${BACKEND_URL}/appointments/${appointment.id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ Status: { status: 'completed' } })
            });

            const checkedResponse = await handleFetchResponse(response, navigate);
            if (!checkedResponse) return;

            if (!checkedResponse.ok) {
                throw new Error(`Failed to update appointment: ${checkedResponse.status}`);
            }

            // Update the appointment in the state
            setAllAppointments(prevAppointments =>
                prevAppointments.map(apt =>
                    apt.id === appointment.id
                        ? { ...apt, status: 'completed' }
                        : apt
                )
            );

            setShowDetailsMenu(null);
            alert('Appointment marked as completed!');
        } catch (err) {
            console.error('Error marking appointment as complete:', err);
            alert(`Error: ${err.message}`);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showDetailsMenu && !event.target.closest('.details-menu-container')) {
                setShowDetailsMenu(null);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [showDetailsMenu]);

    const getStatusBadgeClass = (status) => {
        const statusLower = status?.toLowerCase() || '';
        switch (statusLower) {
            case 'pending':
                return 'status-badge status-pending';
            case 'assigned':
                return 'status-badge status-assigned';
            case 'accepted':
                return 'status-badge status-accepted';
            case 'rejected':
                return 'status-badge status-rejected';
            case 'serviced':
                return 'status-badge status-serviced';
            case 'reviewed':
                return 'status-badge status-reviewed';
            case 'completed':
                return 'status-badge status-completed';
            case 'cancelled':
                return 'status-badge status-cancelled';
            default:
                return 'status-badge';
        }
    };

    const getUserSchedule = (userId, userType) => {
        if (userType === 'service_worker') {
            return allAppointments.filter(apt => apt.psw_id === userId);
        } else if (userType === 'patient') {
            return allAppointments.filter(apt => apt.user_id === userId);
        }
        return [];
    };

    // Calculate activity data from appointments with filters
    const calculateActivityData = (appointments) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Initialize data for the past 30 days
        const dataByDate = {};
        for (let i = 29; i >= 0; i--) {
            const date = new Date(today);
            date.setDate(date.getDate() - i);
            const dateKey = date.toISOString().split('T')[0];
            dataByDate[dateKey] = 0;
        }
        
        // Filter appointments based on active filters
        const filteredAppointments = appointments.filter(apt => {
            // Apply status filter
            if (activityStatusFilter !== 'all' && apt.status?.toLowerCase() !== activityStatusFilter.toLowerCase()) {
                return false;
            }
            // Apply PSW filter
            if (activityPswFilter !== 'all' && apt.psw_id !== activityPswFilter) {
                return false;
            }
            // Apply user (patient) filter
            if (activityUserFilter !== 'all' && apt.user_id !== activityUserFilter) {
                return false;
            }
            return true;
        });
        
        // Count appointments per day
        filteredAppointments.forEach(apt => {
            try {
                const aptDate = new Date(apt.start_date);
                aptDate.setHours(0, 0, 0, 0);
                const dateKey = aptDate.toISOString().split('T')[0];
                if (dateKey in dataByDate) {
                    dataByDate[dateKey]++;
                }
            } catch (err) {
                console.error('Error processing appointment date:', err);
            }
        });
        
        // Convert to array format
        const newActivityData = [];
        for (let i = 29; i >= 0; i--) {
            const date = new Date(today);
            date.setDate(date.getDate() - i);
            const dateKey = date.toISOString().split('T')[0];
            newActivityData.push({
                date: new Date(date),
                count: dataByDate[dateKey] || 0
            });
        }
        
        return newActivityData;
    };

    // Calculate activity data when appointments or filters change
    useEffect(() => {
        setActivityLoading(true);
        try {
            const calculatedData = calculateActivityData(allAppointments);
            setActivityData(calculatedData);
        } catch (err) {
            console.error('Error calculating activity data:', err);
            setActivityData([]);
        } finally {
            setActivityLoading(false);
        }
    }, [allAppointments, activityStatusFilter, activityPswFilter, activityUserFilter]);

    // Filter upcoming appointments by status and service
    useEffect(() => {
        let filtered = upcomingAppointments;

        if (upcomingStatusFilter !== 'all') {
            filtered = filtered.filter(apt => apt.status === upcomingStatusFilter);
        }

        if (upcomingServiceFilter !== 'all') {
            filtered = filtered.filter(apt => {
                const service = apt.service_name || apt.service;
                return service === upcomingServiceFilter;
            });
        }

        setFilteredUpcomingAppointments(filtered);
    }, [upcomingAppointments, upcomingStatusFilter, upcomingServiceFilter]);

    if (loading) {
        return (
            <div className="dashboard-page">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p className="loading-message">Loading dashboard data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-page">
            <DashboardSidebar
                sidebarCollapsed={sidebarCollapsed}
                onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
                profileTitle="Admin Dashboard"
                profileSubtitle="Master Control Panel"
                buttons={[
                    {
                        icon: '📊',
                        text: 'View Reports',
                        onClick: handleViewReports,
                        ariaLabel: 'View reports',
                        title: 'View Reports'
                    },
                    {
                        icon: '➕',
                        text: 'Create Appointment',
                        onClick: () => setShowCreateAppointmentModal(true),
                        ariaLabel: 'Create new appointment',
                        title: 'Create Appointment'
                    },
                    {
                        icon: '👥',
                        text: 'View All Users',
                        onClick: () => navigate(`${FRONTEND_URL}/view-users`),
                        ariaLabel: 'View all users and PSWs',
                        title: 'View All Users'
                    },
                    {
                        icon: '📨',
                        text: 'Invite User',
                        onClick: () => setShowInviteModal(true),
                        ariaLabel: 'Invite User',
                        title: 'Invite User'
                    },
                    {
                        icon: '🚪',
                        text: 'Logout',
                        onClick: handleLogout,
                        className: 'logout-button',
                        ariaLabel: 'Logout',
                        title: 'Logout'
                    }
                ]}
            />

            <div className="dashboard-main">
                <section className="dashboard-card">
                    <div className="dashboard-title-row">
                        <h3>Upcoming Appointments</h3>
                        <small>{filteredUpcomingAppointments.length} of {upcomingAppointments.length} items</small>
                    </div>

                    {/* Filter Section */}
                    <div style={{ marginBottom: '20px', padding: '16px', background: '#f8f9fa', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <label style={{ fontWeight: '600' }}>Filter by:</label>
                            <select
                                value={upcomingStatusFilter}
                                onChange={(e) => setUpcomingStatusFilter(e.target.value)}
                                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                            >
                                <option value="all">All Statuses</option>
                                {statuses.map(status => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>

                            <select
                                value={upcomingServiceFilter}
                                onChange={(e) => setUpcomingServiceFilter(e.target.value)}
                                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                            >
                                <option value="all">All Services</option>
                                {availableServices.map(service => (
                                    <option key={service} value={service}>{service}</option>
                                ))}
                            </select>

                            {(upcomingStatusFilter !== 'all' || upcomingServiceFilter !== 'all') && (
                                <button
                                    onClick={() => {
                                        setUpcomingStatusFilter('all');
                                        setUpcomingServiceFilter('all');
                                    }}
                                    style={{
                                        padding: '8px 12px',
                                        background: '#e2e8f0',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}
                                >
                                    Clear Filters
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="table-container">
                        <table className="dashboard-table" role="table" aria-label="Upcoming appointments">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Time</th>
                                    <th>Service Worker</th>
                                    <th>Patient</th>
                                    <th>Service</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUpcomingAppointments.map((a, index) => {
                                    const { date, time } = formatDateTime(a.start_date);
                                    const swName = getServiceWorkerName(a.psw_id);
                                    const patientName = getPatientName(a.user_id);
                                    
                                    return (
                                        <tr key={a.id} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                                            <td>{date}</td>
                                            <td>{time}</td>
                                            <td>
                                                <button
                                                    onClick={() => handleViewUserInfo(a.psw_id, 'service_worker')}
                                                    style={{ 
                                                        background: 'none', 
                                                        border: 'none', 
                                                        color: '#3b82f6', 
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline'
                                                    }}
                                                >
                                                    {swName}
                                                </button>
                                            </td>
                                            <td>
                                                <button
                                                    onClick={() => handleViewUserInfo(a.user_id, 'patient')}
                                                    style={{ 
                                                        background: 'none', 
                                                        border: 'none', 
                                                        color: '#3b82f6', 
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline'
                                                    }}
                                                >
                                                    {patientName}
                                                </button>
                                            </td>
                                            <td>{getServiceNames(a)}</td>
                                            <td>
                                                <span className={getStatusBadgeClass(a.status)}>
                                                    {a.status || 'N/A'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="actions-cell">
                                                    <div 
                                                        className="details-menu-container" 
                                                        data-appointment-id={a.id}
                                                        onMouseEnter={(e) => handleMouseEnter(a.id, e)}
                                                        onMouseLeave={handleMouseLeave}
                                                    >
                                                        <button
                                                            className="details-button"
                                                            aria-label="View details"
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <circle cx="8" cy="3" r="1.5" fill="currentColor"/>
                                                                <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
                                                                <circle cx="8" cy="13" r="1.5" fill="currentColor"/>
                                                            </svg>
                                                        </button>
                                                        {showDetailsMenu === a.id && (
                                                            <div 
                                                                className="details-menu"
                                                                style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
                                                                onMouseEnter={() => {
                                                                    // Clear any pending hide timeout when hovering over menu
                                                                    if (menuHideTimeoutRef.current) {
                                                                        clearTimeout(menuHideTimeoutRef.current);
                                                                        menuHideTimeoutRef.current = null;
                                                                    }
                                                                    setShowDetailsMenu(a.id);
                                                                }}
                                                                onMouseLeave={handleMouseLeave}
                                                            >
                                                                <button
                                                                    className="details-menu-item"
                                                                    onClick={() => {
                                                                        setSelectedAppointment(a);
                                                                        setShowAppointmentDetails(true);
                                                                        setShowDetailsMenu(null);
                                                                    }}
                                                                >
                                                                    <span className="menu-icon">👁️</span>
                                                                    View Full Details
                                                                </button>
                                                                <button
                                                                    className="details-menu-item"
                                                                    onClick={() => {
                                                                        handleViewUserInfo(a.psw_id, 'service_worker');
                                                                        setShowDetailsMenu(null);
                                                                    }}
                                                                >
                                                                    <span className="menu-icon">👤</span>
                                                                    View Service Worker Info
                                                                </button>
                                                                <button
                                                                    className="details-menu-item"
                                                                    onClick={() => {
                                                                        handleViewUserInfo(a.user_id, 'patient');
                                                                        setShowDetailsMenu(null);
                                                                    }}
                                                                >
                                                                    <span className="menu-icon">🏥</span>
                                                                    View Patient Info
                                                                </button>
                                                                {(a.status?.toLowerCase() === 'pending' || a.status?.toLowerCase() === 'rejected') && (
                                                                    <button
                                                                        className="details-menu-item"
                                                                        onClick={(e) => handleOpenAssignModal(a, e)}
                                                                    >
                                                                        <span className="menu-icon">👨‍⚕️</span>
                                                                        Assign Service Worker
                                                                    </button>
                                                                )}
                                                                {a.status?.toLowerCase() === 'reviewed' && (
                                                                    <>
                                                                    <button
                                                                        className="details-menu-item"
                                                                        onClick={() => {
                                                                            setSelectedReportId(a.report_id || a.id);
                                                                            setShowReportModal(true);
                                                                            setShowDetailsMenu(null);
                                                                        }}
                                                                    >
                                                                        <span className="menu-icon">📄</span>
                                                                        View Report
                                                                    </button>
                                                                    <button
                                                                        className="details-menu-item"
                                                                        onClick={() => handleMarkAsComplete(a)}
                                                                    >
                                                                        <span className="menu-icon">✅</span>
                                                                        Mark as Complete
                                                                    </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filteredUpcomingAppointments.length === 0 && (
                                    <tr>
                                        <td colSpan="7" className="empty-state">
                                            <div className="empty-state-content">
                                                <span className="empty-icon">📅</span>
                                                <p>No upcoming appointments</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>

                <section className="dashboard-card">
                    <div className="dashboard-title-row">
                        <h3>Past Appointments</h3>
                        <small>{filteredPastAppointments.length} of {pastAppointments.length} items</small>
                    </div>

                    {/* Filter Section */}
                    <div style={{ marginBottom: '20px', padding: '16px', background: '#f8f9fa', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <label style={{ fontWeight: '600' }}>Filter by:</label>
                            <select
                                value={filterType}
                                onChange={(e) => {
                                    setFilterType(e.target.value);
                                    setFilterValue('');
                                }}
                                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                            >
                                <option value="all">All Appointments</option>
                                <option value="service_worker">Service Worker</option>
                                <option value="patient">Patient</option>
                            </select>
                            
                            {filterType === 'service_worker' && (
                                <select
                                    value={filterValue}
                                    onChange={(e) => setFilterValue(e.target.value)}
                                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', minWidth: '200px' }}
                                >
                                    <option value="">Select Service Worker</option>
                                    {availableServiceWorkers.map(sw => (
                                        <option key={sw.psw_id || sw.id} value={sw.psw_id || sw.id}>
                                            {sw.name || `${sw.first_name || ''} ${sw.last_name || ''}`.trim() || sw.psw_id || sw.id}
                                        </option>
                                    ))}
                                </select>
                            )}
                            
                            {filterType === 'patient' && (
                                <select
                                    value={filterValue}
                                    onChange={(e) => setFilterValue(e.target.value)}
                                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', minWidth: '200px' }}
                                >
                                    <option value="">Select Patient</option>
                                    {availableUsers.map(patient => (
                                        <option key={patient.user_id || patient.id} value={patient.user_id || patient.id}>
                                            {patient.name || `${patient.first_name || ''} ${patient.last_name || ''}`.trim() || patient.user_id || patient.id}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    </div>

                    <div className="table-container">
                        <table className="dashboard-table" role="table" aria-label="Past appointments">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Time</th>
                                    <th>Service Worker</th>
                                    <th>Patient</th>
                                    <th>Service</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPastAppointments.map((a, index) => {
                                    const { date, time } = formatDateTime(a.start_date);
                                    const swName = getServiceWorkerName(a.psw_id);
                                    const patientName = getPatientName(a.user_id || a.user_id);
                                    
                                    return (
                                        <tr key={a.id} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                                            <td>{date}</td>
                                            <td>{time}</td>
                                            <td>
                                                <button
                                                    onClick={() => handleViewUserInfo(a.psw_id, 'service_worker')}
                                                    style={{ 
                                                        background: 'none', 
                                                        border: 'none', 
                                                        color: '#3b82f6', 
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline'
                                                    }}
                                                >
                                                    {swName}
                                                </button>
                                            </td>
                                            <td>
                                                <button
                                                    onClick={() => handleViewUserInfo(a.user_id, 'patient')}
                                                    style={{ 
                                                        background: 'none', 
                                                        border: 'none', 
                                                        color: '#3b82f6', 
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline'
                                                    }}
                                                >
                                                    {patientName}
                                                </button>
                                            </td>
                                            <td>{getServiceNames(a)}</td>
                                            <td>
                                                <span className={getStatusBadgeClass(a.status)}>
                                                    {a.status || 'N/A'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="actions-cell">
                                                    <div 
                                                        className="details-menu-container" 
                                                        data-appointment-id={a.id}
                                                        onMouseEnter={(e) => handleMouseEnter(a.id, e)}
                                                        onMouseLeave={handleMouseLeave}
                                                    >
                                                        <button
                                                            className="details-button"
                                                            aria-label="View details"
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <circle cx="8" cy="3" r="1.5" fill="currentColor"/>
                                                                <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
                                                                <circle cx="8" cy="13" r="1.5" fill="currentColor"/>
                                                            </svg>
                                                        </button>
                                                        {showDetailsMenu === a.id && (
                                                            <div 
                                                                className="details-menu"
                                                                style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
                                                                onMouseEnter={() => {
                                                                    // Clear any pending hide timeout when hovering over menu
                                                                    if (menuHideTimeoutRef.current) {
                                                                        clearTimeout(menuHideTimeoutRef.current);
                                                                        menuHideTimeoutRef.current = null;
                                                                    }
                                                                    setShowDetailsMenu(a.id);
                                                                }}
                                                                onMouseLeave={handleMouseLeave}
                                                            >
                                                                <button
                                                                    className="details-menu-item"
                                                                    onClick={() => {
                                                                        setSelectedAppointment(a);
                                                                        setShowAppointmentDetails(true);
                                                                        setShowDetailsMenu(null);
                                                                    }}
                                                                >
                                                                    <span className="menu-icon">👁️</span>
                                                                    View Full Details
                                                                </button>
                                                                <button
                                                                    className="details-menu-item"
                                                                    onClick={() => {
                                                                        handleViewUserInfo(a.psw_id, 'service_worker');
                                                                        setShowDetailsMenu(null);
                                                                    }}
                                                                >
                                                                    <span className="menu-icon">👤</span>
                                                                    View Service Worker Info
                                                                </button>
                                                                <button
                                                                    className="details-menu-item"
                                                                    onClick={() => {
                                                                        handleViewUserInfo(a.user_id, 'patient');
                                                                        setShowDetailsMenu(null);
                                                                    }}
                                                                >
                                                                    <span className="menu-icon">🏥</span>
                                                                    View Patient Info
                                                                </button>
                                                                <button
                                                                    className="details-menu-item"
                                                                    onClick={() => {
                                                                        setSelectedReportId(a.report_id || a.id);
                                                                        setShowReportModal(true);
                                                                        setShowDetailsMenu(null);
                                                                    }}
                                                                >
                                                                    <span className="menu-icon">📄</span>
                                                                    View Report
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filteredPastAppointments.length === 0 && (
                                    <tr>
                                        <td colSpan="7" className="empty-state">
                                            <div className="empty-state-content">
                                                <span className="empty-icon">📅</span>
                                                <p>No past appointments</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* 30-Day Activity Section */}
                <section className="dashboard-card">
                    <div className="dashboard-title-row">
                        <h3>Analytics</h3>
                        <small>
                            {activityStatusFilter === 'all' 
                                ? 'All statuses' 
                                : `Status: ${activityStatusFilter.charAt(0).toUpperCase() + activityStatusFilter.slice(1)}`
                            }
                            {activityPswFilter !== 'all' && ` • PSW: ${getServiceWorkerName(activityPswFilter)}`}
                            {activityUserFilter !== 'all' && ` • User: ${getPatientName(activityUserFilter)}`}
                        </small>
                    </div>
                    <div style={{ padding: '20px' }}>
                        {/* Activity Filters */}
                        <div style={{ marginBottom: '20px', padding: '16px', background: '#f8f9fa', borderRadius: '8px' }}>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                                <label style={{ fontWeight: '600' }}>Filter by:</label>
                                
                                {/* Status Filter */}
                                <select
                                    value={activityStatusFilter}
                                    onChange={(e) => setActivityStatusFilter(e.target.value)}
                                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                                >
                                    <option value="all">All Statuses</option>
                                    {statuses.map((status, idx) => (
                                        <option key={idx} value={status.toLowerCase()}>
                                            {status}
                                        </option>
                                    ))}
                                </select>
                                
                                {/* PSW Filter */}
                                <select
                                    value={activityPswFilter}
                                    onChange={(e) => setActivityPswFilter(e.target.value)}
                                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', minWidth: '180px' }}
                                >
                                    <option value="all">All Service Workers</option>
                                    {Array.isArray(serviceWorkers) && serviceWorkers.map(sw => (
                                        <option key={sw.psw_id || sw.user_id} value={sw.psw_id || sw.user_id}>
                                            {sw.name || `${sw.first_name || ''} ${sw.last_name || ''}`.trim() || sw.psw_id || sw.user_id}
                                        </option>
                                    ))}
                                </select>
                                
                                {/* User (Patient) Filter */}
                                <select
                                    value={activityUserFilter}
                                    onChange={(e) => setActivityUserFilter(e.target.value)}
                                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', minWidth: '180px' }}
                                >
                                    <option value="all">All Users/Users</option>
                                    {Array.isArray(users) && users.map(patient => (
                                        <option key={patient.user_id || patient.id} value={patient.user_id || patient.id}>
                                            {patient.name || `${patient.first_name || ''} ${patient.last_name || ''}`.trim() || patient.user_id || patient.id}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {activityLoading ? (
                            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
                                <div className="loading-spinner" style={{ margin: '0 auto', marginBottom: '12px' }}></div>
                                <p>Calculating activity data...</p>
                            </div>
                        ) : (
                            <>
                        {/* Bar Chart */}
                        <div 
                            id="chart-container"
                            style={{ 
                                marginBottom: '30px',
                                position: 'relative',
                                paddingLeft: '60px',
                                paddingTop: '20px',
                                overflowX: 'auto',
                                overflowY: 'hidden'
                            }}
                        >
                            {/* Y-axis labels with grid lines */}
                            <div style={{
                                position: 'absolute',
                                left: '0',
                                top: '20px',
                                bottom: '50px',
                                width: '50px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                fontSize: '12px',
                                color: '#64748b',
                                fontWeight: '500',
                                borderRight: '2px solid #e2e8f0',
                                paddingRight: '10px'
                            }}>
                                {(() => {
                                    if (activityData.length === 0) return null;
                                    const maxCount = Math.max(...activityData.map(d => d.count), 1);
                                    // Round up to nearest nice number
                                    const niceMax = Math.ceil(maxCount / 5) * 5;
                                    const steps = 5;
                                    const labels = [];
                                    for (let i = steps; i >= 0; i--) {
                                        labels.push(Math.round((niceMax / steps) * i));
                                    }
                                    return labels.map((label, idx) => (
                                        <div key={idx} style={{ 
                                            textAlign: 'right',
                                            position: 'relative',
                                            paddingRight: '8px'
                                        }}>
                                            {label}
                                        </div>
                                    ));
                                })()}
                            </div>
                            
                            {/* Grid lines */}
                            <div style={{
                                position: 'absolute',
                                left: '50px',
                                right: '0',
                                top: '20px',
                                bottom: '50px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                pointerEvents: 'none'
                            }}>
                                {(() => {
                                    const lines = [];
                                    for (let i = 0; i <= 5; i++) {
                                        lines.push(
                                            <div key={i} style={{
                                                borderTop: i === 5 ? '2px solid #e2e8f0' : '1px solid #f1f5f9',
                                                width: '100%'
                                            }} />
                                        );
                                    }
                                    return lines;
                                })()}
                            </div>
                            
                            {/* Bars container */}
                            {(() => {
                                if (activityData.length === 0) return null;
                                
                                const maxCount = Math.max(...activityData.map(d => d.count), 1);
                                const niceMax = Math.ceil(maxCount / 5) * 5;
                                const chartHeight = 280; // Fixed height in pixels
                                
                                return (
                                    <div 
                                        id="bars-container"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'flex-end',
                                            justifyContent: 'space-between',
                                            height: `${chartHeight}px`,
                                            padding: '0 10px 50px 20px',
                                            borderBottom: '2px solid #e2e8f0',
                                            borderLeft: '2px solid #e2e8f0',
                                            gap: '4px',
                                            position: 'relative'
                                        }}
                                    >
                                        {activityData.map((day, index) => {
                                            const barHeightPx = day.count > 0 
                                                ? Math.max((day.count / niceMax) * chartHeight, 4) 
                                                : 0;
                                            const isToday = day.date.toDateString() === new Date().toDateString();
                                            const isWeekend = day.date.getDay() === 0 || day.date.getDay() === 6;
                                            
                                            return (
                                                <div 
                                                    key={index}
                                                    style={{
                                                        flex: 1,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-end',
                                                        position: 'relative',
                                                        minWidth: '12px',
                                                        maxWidth: '24px',
                                                        height: '100%'
                                                    }}
                                                >
                                                    {/* Number on top of bar */}
                                                    {day.count > 0 && (
                                                        <div style={{
                                                            position: 'absolute',
                                                            bottom: `${barHeightPx + 4}px`,
                                                            fontSize: '10px',
                                                            fontWeight: '600',
                                                            color: '#1e293b',
                                                            whiteSpace: 'nowrap',
                                                            zIndex: 10,
                                                            transform: 'translateX(-50%)',
                                                            left: '50%'
                                                        }}>
                                                            {day.count}
                                                        </div>
                                                    )}
                                                    
                                                    {/* Bar */}
                                                    <div 
                                                        style={{
                                                            width: '100%',
                                                            height: `${barHeightPx}px`,
                                                            minHeight: day.count > 0 ? '4px' : '0',
                                                            background: isToday 
                                                                ? '#3b82f6' 
                                                                : isWeekend 
                                                                    ? '#94a3b8' 
                                                                    : '#60a5fa',
                                                            borderRadius: '4px 4px 0 0',
                                                            transition: 'all 0.2s ease',
                                                            cursor: 'pointer',
                                                            border: isToday ? '2px solid #1e40af' : 'none',
                                                            boxShadow: isToday ? '0 2px 8px rgba(59, 130, 246, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.opacity = '0.85';
                                                            e.currentTarget.style.transform = 'scaleY(1.05)';
                                                            e.currentTarget.style.transformOrigin = 'bottom';
                                                            
                                                            // Calculate tooltip position relative to the chart container
                                                            const barElement = e.currentTarget;
                                                            const chartContainer = document.getElementById('chart-container');
                                                            if (chartContainer) {
                                                                const containerRect = chartContainer.getBoundingClientRect();
                                                                const barRect = barElement.getBoundingClientRect();
                                                                // Position tooltip centered above the bar, relative to chart container
                                                                setTooltipPosition({
                                                                    x: barRect.left + barRect.width / 2 - containerRect.left,
                                                                    y: barRect.top - containerRect.top - 10
                                                                });
                                                            }
                                                            setHoveredBar(index);
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.opacity = '1';
                                                            e.currentTarget.style.transform = 'scaleY(1)';
                                                            setHoveredBar(null);
                                                        }}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })()}
                            
                            {/* Hover Tooltip */}
                            {hoveredBar !== null && activityData.length > 0 && (() => {
                                const day = activityData[hoveredBar];
                                const dayOfWeek = day.date.toLocaleDateString('en-US', { weekday: 'long' });
                                const dateStr = day.date.toLocaleDateString('en-US', { 
                                    month: 'long', 
                                    day: 'numeric',
                                    year: 'numeric'
                                });
                                
                                return (
                                    <div style={{
                                        position: 'absolute',
                                        left: `${tooltipPosition.x}px`,
                                        top: `${tooltipPosition.y}px`,
                                        transform: 'translateX(-50%) translateY(-100%)',
                                        background: '#1e293b',
                                        color: '#ffffff',
                                        padding: '12px 16px',
                                        borderRadius: '8px',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                                        zIndex: 1000,
                                        whiteSpace: 'nowrap',
                                        pointerEvents: 'none',
                                        marginBottom: '8px',
                                        minWidth: '180px'
                                    }}>
                                        <div style={{ 
                                            marginBottom: '6px',
                                            fontSize: '11px',
                                            color: '#cbd5e1',
                                            fontWeight: '400',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            {dayOfWeek}
                                        </div>
                                        <div style={{ 
                                            marginBottom: '8px',
                                            fontSize: '13px',
                                            fontWeight: '600',
                                            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                                            paddingBottom: '6px'
                                        }}>
                                            {dateStr}
                                        </div>
                                        <div style={{ 
                                            fontSize: '16px',
                                            fontWeight: '700',
                                            color: '#60a5fa'
                                        }}>
                                            {day.count} appointment{day.count !== 1 ? 's' : ''}
                                        </div>
                                        {/* Arrow pointing down */}
                                        <div style={{
                                            position: 'absolute',
                                            bottom: '-8px',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            width: 0,
                                            height: 0,
                                            borderLeft: '8px solid transparent',
                                            borderRight: '8px solid transparent',
                                            borderTop: '8px solid #1e293b'
                                        }} />
                                    </div>
                                );
                            })()}
                            
                            {/* X-axis labels (bottom) */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontSize: '10px',
                                color: '#64748b',
                                marginTop: '8px',
                                paddingLeft: '20px',
                                paddingRight: '10px',
                                fontWeight: '500',
                                minWidth: 'min-content'
                            }}>
                                {activityData.map((day, index) => {
                                    if (index % 5 === 0 || index === activityData.length - 1) {
                                        return (
                                            <div key={index} style={{ 
                                                textAlign: 'center', 
                                                minWidth: '24px',
                                                fontSize: '10px'
                                            }}>
                                                {day.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                            </div>
                                        );
                                    }
                                    return <div key={index} style={{ minWidth: '24px' }} />;
                                })}
                            </div>
                        </div>
                        
                        {/* Summary Stats by Status */}
                        <div style={{ 
                            display: 'flex', 
                            gap: '20px', 
                            padding: '16px', 
                            background: '#f8f9fa', 
                            borderRadius: '8px',
                            flexWrap: 'wrap'
                        }}>
                            {statuses.map((status, idx) => {
                                const statusCount = allAppointments.filter(apt => 
                                    apt.status?.toLowerCase() === status.toLowerCase()
                                ).length;
                                return (
                                    <div key={idx}>
                                        <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                                            {status}
                                        </div>
                                        <div style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b' }}>
                                            {statusCount}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Filtered Stats based on Activity Filters */}
                        {(activityStatusFilter !== 'all' || activityPswFilter !== 'all' || activityUserFilter !== 'all') && (
                            <div style={{ 
                                marginTop: '20px',
                                display: 'flex', 
                                gap: '20px', 
                                padding: '16px', 
                                background: '#eff6ff', 
                                borderRadius: '8px',
                                border: '1px solid #bfdbfe',
                                flexWrap: 'wrap'
                            }}>
                                <div style={{ fontSize: '12px', color: '#1e40af', fontWeight: '600', marginBottom: '12px', width: '100%' }}>
                                    Filtered Stats
                                    {activityStatusFilter !== 'all' && ` (Status: ${activityStatusFilter})`}
                                    {activityPswFilter !== 'all' && ` (PSW: ${getServiceWorkerName(activityPswFilter)})`}
                                    {activityUserFilter !== 'all' && ` (User: ${getPatientName(activityUserFilter)})`}
                                </div>
                                {statuses.map((status, idx) => {
                                    let filteredApptsForStats = allAppointments;
                                    
                                    // Apply status filter
                                    if (activityStatusFilter !== 'all') {
                                        filteredApptsForStats = filteredApptsForStats.filter(apt => 
                                            apt.status?.toLowerCase() === activityStatusFilter.toLowerCase()
                                        );
                                    }
                                    
                                    // Apply PSW filter
                                    if (activityPswFilter !== 'all') {
                                        filteredApptsForStats = filteredApptsForStats.filter(apt => 
                                            apt.psw_id === activityPswFilter
                                        );
                                    }
                                    
                                    // Apply user/patient filter
                                    if (activityUserFilter !== 'all') {
                                        filteredApptsForStats = filteredApptsForStats.filter(apt => 
                                            apt.user_id === activityUserFilter
                                        );
                                    }
                                    
                                    const filteredStatusCount = filteredApptsForStats.filter(apt => 
                                        apt.status?.toLowerCase() === status.toLowerCase()
                                    ).length;
                                    
                                    return (
                                        <div key={idx}>
                                            <div style={{ fontSize: '12px', color: '#1e40af', marginBottom: '4px' }}>
                                                {status}
                                            </div>
                                            <div style={{ fontSize: '18px', fontWeight: '600', color: '#1e40af' }}>
                                                {filteredStatusCount}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                            </>
                        )}
                    </div>
                </section>
            </div>

            {/* Appointment Details Modal */}
            <AppointmentDetailsModal
                isOpen={showAppointmentDetails}
                appointment={selectedAppointment}
                onClose={handleCloseAppointmentDetails}
                formatDateTime={formatDateTime}
                getServiceWorkerName={getServiceWorkerName}
                getPatientName={getPatientName}
                getStatusBadgeClass={getStatusBadgeClass}
                onEditAppointment={handleEditAppointment}
                onDeleteAppointment={handleDeleteAppointment}
            />

            {/* User Information Modal */}
            <UserInfoModal
                isOpen={showUserInfo}
                user={selectedUser}
                onClose={handleCloseUserInfo}
                formatDateTime={formatDateTime}
                getStatusBadgeClass={getStatusBadgeClass}
                getUserSchedule={getUserSchedule}
            />

            {/* Assign Service Worker Modal */}
            {showAssignModal && appointmentToAssign && (
                <div className="modal-overlay" onClick={handleCloseAssignModal}>
                    <div className="modal-content appointment-details-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Assign Service Worker</h2>
                            <button className="modal-close" onClick={handleCloseAssignModal} aria-label="Close modal">
                                ×
                            </button>
                        </div>
                        <div className="appointment-details-content">
                            <div className="detail-row">
                                <span className="detail-label">Patient:</span>
                                <span className="detail-value">{getPatientName(appointmentToAssign.user_id)}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Service:</span>
                                <span className="detail-value">{getServiceNames(appointmentToAssign)}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Appointment Time:</span>
                                <span className="detail-value">
                                    {formatDateTime(appointmentToAssign.start_date).date} at{' '}
                                    {formatDateTime(appointmentToAssign.start_date).time}
                                </span>
                            </div>
                            
                            <div style={{ marginTop: '20px', marginBottom: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#1e293b' }}>
                                    Select Service Worker:
                                </label>
                                <select
                                    value={selectedServiceWorker}
                                    onChange={(e) => setSelectedServiceWorker(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '10px 12px',
                                        borderRadius: '6px',
                                        border: '1px solid #e2e8f0',
                                        fontSize: '14px',
                                        fontFamily: 'inherit',
                                        backgroundColor: '#ffffff',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <option value="">-- Select Service Worker --</option>
                                    {Array.isArray(serviceWorkers) && serviceWorkers.map(sw => (
                                        <option key={sw.psw_id || sw.id} value={sw.psw_id || sw.id}>
                                            {sw.name || `${sw.first_name || ''} ${sw.last_name || ''}`.trim() || sw.psw_id || sw.id}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {assignmentError && (
                                <div style={{
                                    padding: '12px 14px',
                                    backgroundColor: '#fee2e2',
                                    color: '#dc2626',
                                    borderRadius: '6px',
                                    marginBottom: '16px',
                                    fontSize: '14px'
                                }}>
                                    {assignmentError}
                                </div>
                            )}
                        </div>
                        <div className="modal-actions">
                            <button
                                type="button"
                                className="modal-button-cancel"
                                onClick={handleCloseAssignModal}
                                disabled={assignmentLoading}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="modal-button-submit"
                                onClick={handleSubmitAssignment}
                                disabled={assignmentLoading || !selectedServiceWorker}
                                style={{
                                    opacity: (assignmentLoading || !selectedServiceWorker) ? 0.6 : 1,
                                    cursor: (assignmentLoading || !selectedServiceWorker) ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {assignmentLoading ? 'Assigning...' : 'Assign'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Profile Modal */}
            <ProfileModal
                showModal={showProfileModal}
                onClose={handleCloseProfileModal}
                profileFormData={profileFormData}
                onInputChange={handleProfileInputChange}
                onSubmit={handleSubmitProfile}
                loading={profileLoading}
                error={profileError}
                success={profileSuccess}
            />

            <ReportModal 
                isOpen={showReportModal}
                onClose={() => {
                    setShowReportModal(false);
                    setSelectedReportId(null);
                }}
                reportId={selectedReportId}
            />

            {/* Invite Modal */}
            {showInviteModal && (
                <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Invite User</h2>
                            <button className="modal-close" onClick={() => setShowInviteModal(false)} aria-label="Close modal">
                                ×
                            </button>
                        </div>
                        <div className="modal-body" style={{ padding: '20px' }}>
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                    Invite Type
                                </label>
                                <div style={{ display: 'flex', gap: '16px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="inviteType"
                                            value="user"
                                            checked={inviteType === 'user'}
                                            onChange={(e) => setInviteType(e.target.value)}
                                            style={{ marginRight: '8px' }}
                                        />
                                        Patient
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="inviteType"
                                            value="psw"
                                            checked={inviteType === 'psw'}
                                            onChange={(e) => setInviteType(e.target.value)}
                                            style={{ marginRight: '8px' }}
                                        />
                                        Service Worker
                                    </label>
                                </div>
                            </div>
                            <div style={{ marginBottom: '20px' }}>
                                <label htmlFor="invite-email" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                    Email Address
                                </label>
                                <input
                                    id="invite-email"
                                    type="email"
                                    value={inviteEmail}
                                    onChange={(e) => setInviteEmail(e.target.value)}
                                    placeholder="Enter email address"
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>
                            {inviteError && (
                                <div style={{ color: '#dc3545', marginBottom: '15px', fontSize: '14px' }}>
                                    {inviteError}
                                </div>
                            )}
                            {inviteSuccess && (
                                <div style={{ color: '#28a745', marginBottom: '15px', fontSize: '14px' }}>
                                    {inviteSuccess}
                                </div>
                            )}
                        </div>
                        <div className="modal-actions">
                            <button
                                type="button"
                                className="modal-button-cancel"
                                onClick={() => {
                                    setShowInviteModal(false);
                                    setInviteEmail('');
                                    setInviteError(null);
                                }}
                                disabled={inviteLoading}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="modal-button-submit"
                                onClick={handleInvite}
                                disabled={inviteLoading || !inviteEmail.trim()}
                            >
                                {inviteLoading ? 'Sending...' : 'Invite'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create/Edit Appointment Modal */}
            <AdminCreateAppointmentModal
                isOpen={showCreateAppointmentModal}
                formData={createAppointmentFormData}
                onFormChange={setCreateAppointmentFormData}
                onSubmit={handleCreateAppointment}
                onClose={() => {
                    setShowCreateAppointmentModal(false);
                    resetAppointmentForm();
                }}
                availableServices={availableServices}
                availablePatients={users}
                availableServiceWorkers={serviceWorkers}
                isLoading={createAppointmentLoading}
                error={createAppointmentError}
                isEditMode={isEditingAppointment}
            />

            {/* Error Message for Create Appointment */}
            {createAppointmentError && (
                <div style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    padding: '15px 20px',
                    borderRadius: '4px',
                    zIndex: 1000,
                    maxWidth: '300px'
                }}>
                    {createAppointmentError}
                </div>
            )}
        </div>
    );
}

